import { useEffect, useMemo, useState } from 'react';
import { supportSessionApi } from '@formosa/shared/api/im';
import { useChat } from '../../../chat/ChatProvider';

function findLatestPrompt(messages, sessionId) {
  const sid = sessionId != null ? String(sessionId) : null;
  let latest = null;
  for (const msg of messages || []) {
    if (msg?.messageType !== 'SYSTEM' || msg?.systemCode !== 'CS_RATING_PROMPT') {
      continue;
    }
    const params = msg.systemParams || {};
    if (sid && params.sessionId != null && String(params.sessionId) !== sid) {
      continue;
    }
    if (!params.agentAdminId) continue;
    latest = {
      sessionId: String(params.sessionId || sid || ''),
      agentAdminId: String(params.agentAdminId),
      source: params.source || 'AGENT_PROMPT',
      clientMessageId: msg.clientMessageId || msg.serverMessageId,
    };
  }
  return latest;
}

/**
 * CS19c：偵測 CS_RATING_PROMPT 系統訊息後顯示 GOOD／BAD／略過卡片。
 */
export default function SupportRatingCard({ sessionId, conversationId }) {
  const { state } = useChat();
  const [dismissedKey, setDismissedKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const timeline = state.messagesByConversation[String(conversationId)] || { items: [] };
  const messages = timeline.items || [];

  const prompt = useMemo(
    () => findLatestPrompt(messages, sessionId),
    [messages, sessionId]
  );

  useEffect(() => {
    setDone(false);
    setError('');
  }, [prompt?.clientMessageId]);

  if (!prompt || done || dismissedKey === prompt.clientMessageId) {
    return null;
  }

  async function submit(rating) {
    setSubmitting(true);
    setError('');
    try {
      await supportSessionApi.submitRating(prompt.sessionId, {
        rating,
        agentAdminId: prompt.agentAdminId,
        source: prompt.source,
      });
      setDone(true);
    } catch (err) {
      setError(err?.message || '送出失敗');
    } finally {
      setSubmitting(false);
    }
  }

  async function skip() {
    setSubmitting(true);
    setError('');
    try {
      await supportSessionApi.skipRating(prompt.sessionId, {
        agentAdminId: prompt.agentAdminId,
      });
      setDismissedKey(prompt.clientMessageId);
    } catch (err) {
      setError(err?.message || '略過失敗');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="support-rating-card">
      <div className="support-rating-card__title">本次服務評價</div>
      <p className="support-rating-card__hint">您的回饋僅用於改善客服品質</p>
      {error ? <div className="support-rating-card__error">{error}</div> : null}
      <div className="support-rating-card__actions">
        <button type="button" className="btn btn-primary" disabled={submitting} onClick={() => submit('GOOD')}>
          好評
        </button>
        <button type="button" className="btn btn-secondary" disabled={submitting} onClick={() => submit('BAD')}>
          需改進
        </button>
        <button type="button" className="btn btn-text" disabled={submitting} onClick={skip}>
          略過
        </button>
      </div>
    </div>
  );
}
